// frontend/src/pages/EquipmentPage.tsx

import { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Button,
  Stack,
  Modal,
  Loader,
  Alert,
  SegmentedControl,
  SimpleGrid,
  Paper,
  ThemeIcon,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconAlertCircle, IconArchive } from '@tabler/icons-react';
import { useTank } from '@/contexts/TankContext';
import { NoTankSelected } from '@/components/common/NoTankSelected';
import {
  useEquipmentList,
  useCreateEquipment,
  useUpdateEquipment,
  useDeleteEquipment,
} from '@/hooks/useEquipment';
import { EquipmentForm } from '@/features/equipment/EquipmentForm';
import { EquipmentCard } from '@/features/equipment/EquipmentCard';
import { EmptyEquipmentState } from '@/features/equipment/EmptyEquipmentState';
import { EquipmentSetupWizard } from '@/features/equipment/EquipmentSetupWizard';
import { EquipmentTimeline } from '@/features/equipment/EquipmentTimeline';
import type { Equipment, EquipmentCreate } from '@/types/equipment';
import { useIsMobile } from '@/hooks/useIsMobile';

type StatusFilter = 'active' | 'retired' | 'all';

export default function EquipmentPage() {
  const { selectedTank } = useTank();
  const isMobile = useIsMobile();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [view, setView] = useState<'grid' | 'timeline'>('grid');
  const [formOpened, { open: openForm, close: closeForm }] = useDisclosure(false);
  const [wizardOpened, { open: openWizard, close: closeWizard }] = useDisclosure(false);
  const [editingEquipment, setEditingEquipment] = useState<Equipment | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Fetch equipment with status filter
  const { data, isLoading, error } = useEquipmentList({
    status: statusFilter === 'all' ? undefined : statusFilter,
  });

  const createMutation = useCreateEquipment();
  const updateMutation = useUpdateEquipment();
  const deleteMutation = useDeleteEquipment();

  const handleOpenCreate = () => {
    setEditingEquipment(null);
    openForm();
  };

  const handleEdit = (equipment: Equipment) => {
    setEditingEquipment(equipment);
    openForm();
  };

  const handleRetire = (equipment: Equipment) => {
    const newStatus = equipment.status === 'active' ? 'retired' : 'active';
    updateMutation.mutate({
      id: equipment.id,
      data: { status: newStatus },
    });
  };

  const handleDeleteClick = (equipmentId: string) => {
    closeForm();
    setEditingEquipment(null);
    setDeleteConfirmId(equipmentId);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmId) {
      deleteMutation.mutate(deleteConfirmId, {
        onSettled: () => {
          setDeleteConfirmId(null);
        },
      });
    }
  };

  const handleFormSubmit = (formData: EquipmentCreate) => {
    if (editingEquipment) {
      updateMutation.mutate(
        { id: editingEquipment.id, data: formData },
        { onSuccess: () => closeForm() }
      );
    } else {
      createMutation.mutate(formData, { onSuccess: () => closeForm() });
    }
  };

  const handleFormCancel = () => {
    closeForm();
    setEditingEquipment(null);
  };

  if (!selectedTank) {
    return (
      <Container size="lg" py="xl">
        <NoTankSelected purpose="to manage its equipment" />
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Title order={1}>Equipment</Title>
          <Button leftSection={<IconPlus size={16} />} onClick={handleOpenCreate}>
            {isMobile ? 'Add' : 'Add Equipment'}
          </Button>
        </Group>

        {/* Filters & View Toggle */}
        <Group justify="space-between">
          <SegmentedControl
            value={statusFilter}
            onChange={(value) => setStatusFilter(value as StatusFilter)}
            data={[
              { label: 'Active', value: 'active' },
              { label: 'Retired', value: 'retired' },
              { label: 'All', value: 'all' },
            ]}
          />
          <SegmentedControl
            value={view}
            onChange={(value) => setView(value as 'grid' | 'timeline')}
            data={[
              { label: 'Grid', value: 'grid' },
              { label: 'Timeline', value: 'timeline' },
            ]}
          />
        </Group>

        {/* Error State */}
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {error instanceof Error ? error.message : 'Failed to load equipment'}
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && (
          <Group justify="center" py="xl">
            <Loader role="status" />
          </Group>
        )}

        {/* Content */}
        {!isLoading && !error && data && (
          <>
            {data.equipment.length === 0 ? (
              statusFilter === 'retired' ? (
                <Paper p="xl" withBorder style={{ textAlign: 'center' }}>
                  <Stack align="center" gap="md">
                    <ThemeIcon size={64} radius="xl" variant="light" color="gray">
                      <IconArchive size={32} />
                    </ThemeIcon>
                    <Text fw={500} size="lg">
                      No retired equipment
                    </Text>
                    <Text c="dimmed" size="sm" maw={300}>
                      Your retired equipment will show up here once something's been retired.
                    </Text>
                  </Stack>
                </Paper>
              ) : (
                <EmptyEquipmentState
                  onSetupClick={openWizard}
                  onAddManualClick={handleOpenCreate}
                />
              )
            ) : view === 'timeline' ? (
              <Paper p="md" withBorder>
                <EquipmentTimeline equipment={data.equipment} onItemClick={handleEdit} />
              </Paper>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
                {data.equipment.map((equipment) => (
                  <EquipmentCard
                    key={equipment.id}
                    equipment={equipment}
                    onEdit={handleEdit}
                    onRetire={handleRetire}
                    onDelete={handleDeleteClick}
                  />
                ))}
              </SimpleGrid>
            )}
          </>
        )}
      </Stack>

      {/* Create/Edit Modal */}
      <Modal
        opened={formOpened}
        onClose={handleFormCancel}
        title={editingEquipment ? 'Edit Equipment' : 'Add Equipment'}
        size="lg"
      >
        <EquipmentForm
          equipment={editingEquipment || undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          onDelete={handleDeleteClick}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={!!deleteConfirmId}
        onClose={() => {
          if (!deleteMutation.isPending) {
            setDeleteConfirmId(null);
          }
        }}
        title="Delete Equipment"
        size="sm"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete this equipment? This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => setDeleteConfirmId(null)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDeleteConfirm}
              loading={deleteMutation.isPending}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Equipment Setup Wizard */}
      <EquipmentSetupWizard
        isOpen={wizardOpened}
        onClose={closeWizard}
      />
    </Container>
  );
}
